
import { Button } from "@/components/ui/button";
import { CheckCircle2, X } from "lucide-react";
import LandingHeader from "@/components/LandingHeader";
import LandingFooter from "@/components/LandingFooter";

const ScalooVsHootsuite = () => {
  return (
    <div className="min-h-screen bg-pastel-blue-light dark:bg-[#121624] text-custom-dark dark:text-gray-100">
      <LandingHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Scaloo vs Hootsuite : Comparatif complet</h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            Découvrez pourquoi Scaloo est la meilleure alternative à Hootsuite pour gérer vos réseaux sociaux.
          </p>
          <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Essayer Scaloo gratuitement
          </Button>
        </div>
        
        {/* Comparison Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Scaloo vs Hootsuite : Les différences clés</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-semibold mb-4 text-indigo-600 dark:text-indigo-400">Scaloo</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Interface intuitive conçue pour les équipes marketing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Gestion intelligente des commentaires avec IA intégrée</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Rapports détaillés et personnalisables</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Support client réactif et en français</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Tarification transparente sans frais cachés</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-semibold mb-4 text-gray-600 dark:text-gray-400">Hootsuite</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Interface complexe avec courbe d'apprentissage élevée</span>
                </li>
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Gestion manuelle des commentaires sans assistance IA</span>
                </li>
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Rapports limités sur les plans de base</span>
                </li>
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Support client lent et souvent en anglais uniquement</span>
                </li>
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Nombreux frais supplémentaires pour des fonctionnalités essentielles</span>
                </li>
              </ul>
            </div>
          </div>
        </section>
        
        {/* Features Comparison */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Comparaison des fonctionnalités</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-indigo-50 dark:bg-indigo-900/30">
                  <th className="p-4 text-left border-b border-gray-200 dark:border-gray-700">Fonctionnalité</th>
                  <th className="p-4 text-center border-b border-gray-200 dark:border-gray-700">Scaloo</th>
                  <th className="p-4 text-center border-b border-gray-200 dark:border-gray-700">Hootsuite</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="p-4 border-b border-gray-200 dark:border-gray-700">Planification des publications</td>
                  <td className="p-4 text-center border-b border-gray-200 dark:border-gray-700">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center border-b border-gray-200 dark:border-gray-700">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="p-4 border-b border-gray-200 dark:border-gray-700">Assistance IA</td>
                  <td className="p-4 text-center border-b border-gray-200 dark:border-gray-700">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center border-b border-gray-200 dark:border-gray-700">
                    <X className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="p-4 border-b border-gray-200 dark:border-gray-700">Support en français</td>
                  <td className="p-4 text-center border-b border-gray-200 dark:border-gray-700">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center border-b border-gray-200 dark:border-gray-700">
                    <X className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="text-center">
          <h2 className="text-3xl font-bold mb-6">Prêt à passer à Scaloo ?</h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Rejoignez des milliers d'entreprises qui ont déjà choisi Scaloo pour leur gestion des réseaux sociaux.
          </p>
          <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Démarrer votre essai gratuit
          </Button>
        </section>
      </main>
      
      <LandingFooter />
    </div>
  );
};

export default ScalooVsHootsuite;
